/** Context for the review-page SecurityError; never changes navigation or handles errors. */
export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const environment = config.public.posthog.environment
  if (environment !== 'dev' && environment !== 'prod') return

  const router = nuxtApp.$router
  // Route categories only: paths and query strings may contain customer data.
  const routeStep = (route: {
    path: string
    query?: Record<string, unknown>
  }) => {
    const designStep = route.path.match(
      /^\/v2\/design\/[^/]+\/(front|inside|review)\/?$/,
    )?.[1]
    if (designStep) return designStep
    if (!/^\/v2\/session\/[^/]+\/?$/.test(route.path)) return 'other'
    // Match the restored-session page's precedence and default without logging query values.
    if (route.query?.step === 'review') return 'review'
    return route.query?.page === '3' ? 'inside' : 'front'
  }
  let navigationPhase = 'idle'
  let fromStep = 'other'
  let toStep = 'other'
  let lifecycle = 'active'
  let reports = 0
  const reported = new WeakSet<object>()

  router.beforeEach((to, from) => {
    navigationPhase = 'beforeEach'
    fromStep = routeStep(from)
    toStep = routeStep(to)
  })
  router.afterEach((_to, _from, failure) => {
    navigationPhase = failure ? 'navigation-failed' : 'afterEach'
  })

  function capture(error: unknown, notificationSource: string) {
    const name =
      error instanceof Error || error instanceof DOMException ? error.name : ''
    const message =
      error instanceof Error || error instanceof DOMException
        ? error.message
        : error
    if (
      name !== 'SecurityError' &&
      message !== 'SecurityError: The operation is insecure.'
    )
      return
    if (reports >= 3) return
    if (typeof error === 'object' && error !== null) {
      if (reported.has(error)) return
      reported.add(error)
    }
    reports++
    try {
      void Promise.resolve(
        nuxtApp.$clientPosthog?.capture('greeting_card_router_security_error', {
          $process_person_profile: false,
          environment,
          service: 'greeting-card-builder',
          notification_source: notificationSource,
          build_id: config.app.buildId,
          navigation_phase: navigationPhase,
          from_step: fromStep,
          to_step: toStep,
          current_step: routeStep(router.currentRoute.value),
          lifecycle,
          visibility: document.visibilityState,
          embedded: window.parent !== window,
        }),
      ).catch(() => {})
    } catch {}
  }

  router.onError((error) => capture(error, 'router.onError'))
  window.addEventListener('error', (event) =>
    capture(event.error ?? event.message, 'window.error'),
  )
  window.addEventListener('unhandledrejection', (event) =>
    capture(event.reason, 'window.unhandledrejection'),
  )
  window.addEventListener('pagehide', () => {
    lifecycle = 'pagehide'
  })
  window.addEventListener('pageshow', () => {
    lifecycle = 'active'
  })
})
